import { useEffect, useMemo, useState } from "react";
import { API, getToken } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExternalLink, Download } from "lucide-react";

export default function ViewDocumentDialog({ propertyId, file, open, onOpenChange }) {
  const [downloadingBlob, setDownloadingBlob] = useState(false);

  // Reset focus when dialog closes
  useEffect(() => {
    if (!open) setDownloadingBlob(false);
  }, [open]);

  const directUrl = useMemo(() => {
    if (!file) return null;
    const token = getToken() || "";
    // Auth via ?auth= query param so the iframe / <img> can load directly without JS fetching the body.
    // This avoids "object can not be cloned" errors on large PDFs in Safari/Firefox.
    return `${API}/properties/${propertyId}/documents/${file.id}/download?auth=${encodeURIComponent(token)}`;
  }, [file, propertyId]);

  if (!file) return null;

  const isImage = (file.content_type || "").startsWith("image/");
  const isPdf = (file.content_type || "") === "application/pdf";

  const downloadFile = async () => {
    if (!directUrl) return;
    setDownloadingBlob(true);
    try {
      const res = await fetch(directUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after a tick so the download starts
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch {
      // Fallback: open the URL — the browser will save it
      window.open(directUrl, "_blank", "noreferrer");
    } finally {
      setDownloadingBlob(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] sm:w-[90vw] max-h-[90vh] flex flex-col p-0 overflow-hidden"
        data-testid="view-doc-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-stone-100">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <DialogTitle className="font-medium tracking-tight truncate">
                {file.original_filename}
              </DialogTitle>
              <p className="text-xs text-stone-500 mt-0.5">
                {file.content_type || "file"}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href={directUrl} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid="view-open-tab">
                  <ExternalLink className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                  Open in new tab
                </Button>
              </a>
              <Button
                size="sm"
                onClick={downloadFile}
                disabled={downloadingBlob}
                className="bg-emerald-900 hover:bg-emerald-800 text-stone-50"
                data-testid="view-download"
              >
                <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                {downloadingBlob ? "Downloading…" : "Download"}
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto bg-stone-100">
          {isImage && (
            <div className="min-h-[70vh] flex items-center justify-center p-6">
              <img
                src={directUrl}
                alt={file.original_filename}
                className="max-w-full max-h-[80vh] object-contain rounded-md shadow-sm"
              />
            </div>
          )}
          {isPdf && (
            <iframe
              src={directUrl}
              title={file.original_filename}
              className="w-full h-[80vh] bg-white"
            />
          )}
          {!isImage && !isPdf && (
            <div className="h-[60vh] flex flex-col items-center justify-center px-6 text-center text-stone-600">
              <p className="mb-4">This file type can&apos;t be previewed inline.</p>
              <Button
                onClick={downloadFile}
                disabled={downloadingBlob}
                className="bg-emerald-900 hover:bg-emerald-800 text-stone-50"
              >
                <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
                {downloadingBlob ? "Downloading…" : "Download to view"}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
